import {Avatar, AvatarGroup, Badge, Popover, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {RosterPopoverTrigger} from '@/components';
import {getInitials} from '@/utils/get-initials.utils';

import {formatBuyerDisplayName, getBuyerAvatarShape} from '../utils';
import {BuyerGroupPopoverContent} from './BuyerGroupPopover';

import type {BuyerDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface BuyerAvatarStackProps {
  buyers: BuyerDTO[];
  assetLabel?: string;
  layout?: 'inline' | 'stacked';
  onViewBuyer: (buyer: BuyerDTO) => void;
}

export const BuyerAvatarStack: FC<BuyerAvatarStackProps> = ({
  buyers,
  assetLabel,
  layout = 'inline',
  onViewBuyer
}) => {
  const {t} = useTranslation('trustAccounts');

  const avatars = buyers.map(buyer => {
    const displayName = formatBuyerDisplayName(buyer.fullName);
    return (
      <Avatar
        key={buyer.id}
        initials={getInitials(displayName)}
        size="lg"
        shape={getBuyerAvatarShape(buyer.partyType)}
        seed={buyer.id}
        noShadow
      />
    );
  });

  if (buyers.length <= 1) {
    return avatars[0];
  }

  const avatarGroup = <AvatarGroup>{avatars}</AvatarGroup>;

  const countBadge = (
    <Badge size="sm">{t('view.buyers.list.header.count', {count: buyers.length})}</Badge>
  );

  return (
    <Popover>
      {/* p-1 gives AvatarGroup's ring room — Button's overflow-clip would otherwise cut it off. */}
      <RosterPopoverTrigger overRowLink className="p-1">
        <div className="flex items-center gap-2">
          {avatarGroup}
          {layout === 'stacked' ? (
            <div className="flex flex-col items-start gap-1">
              {countBadge}
              {assetLabel && (
                <Typography size="xs" color="tertiary">
                  {assetLabel}
                </Typography>
              )}
            </div>
          ) : (
            countBadge
          )}
        </div>
      </RosterPopoverTrigger>
      <BuyerGroupPopoverContent buyers={buyers} assetLabel={assetLabel} onViewBuyer={onViewBuyer} />
    </Popover>
  );
};
